import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import TimeSeriesExplorer from './TimeSeriesExplorer'

// Which mi_metrics metric each lens charts over time (mirrors the backend LENSES map
// in services/marketingIntel.js). Drives the financial-style TimeSeriesExplorer.
const LENS_METRIC = {
  exec: 'revenue', acq: 'leads', conv: 'conversions', seo: 'sessions',
  commerce: 'revenue', retention: 'revenue', forecast: 'revenue',
}

// Intelligence Briefings — the same trusted, connected data drawn as a lens rail leadership
// actually reads. Pick a lens (Executive / Acquisition / Conversion / SEO / Commerce /
// Retention / Forecast) and the Brain forecasts where you're heading, explains why, and hands
// you the next move. CSS is ported verbatim from the verified prototype (scoped under .ib-*),
// theme tokens are self-contained (light + dark + prefers-color-scheme), and every chart is
// inline SVG (CSP-safe, no chart libraries, no external icon libs).
//
// DATA: on lens change we GET /api/v1/marketing-intel/briefing/:lens and render
// {elab,val,delta,up,hist,fc,forecast,narr,sugs,sec}. If the response carries sample:true — or
// the endpoint 404s / errors and we fall back to the prototype's built-in mock — a small
// "sample — connect sources for live data" ribbon is shown so nothing fabricated reads as live.
const API = '/api/v1/marketing-intel'

const fmtDate = (d) => d.toISOString().slice(0, 10)
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d }

// ---- inline icon set (reused verbatim from the prototype's ICONS map) ------
const IC = {
  gauge: '<path d="M12 14a2 2 0 1 0-2-2"/><path d="M2 12a10 10 0 0 1 20 0"/><path d="m12 12 4-4"/>',
  target: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1"/>',
  filter: '<path d="M22 3H2l8 9.5V19l4 2v-8.5z"/>',
  globe: '<circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z"/>',
  bag: '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>',
  repeat: '<path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/>',
  crystal: '<path d="M12 3 4 9l8 12 8-12z"/><path d="M4 9h16"/><path d="M12 3v18"/>',
  spark: '<path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"/>',
  bulb: '<path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1h6c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z"/>',
  trend: '<path d="M22 7l-8.5 8.5-5-5L2 17"/><path d="M16 7h6v6"/>',
}
const Svg = ({ name, className }) => (
  <svg className={className} viewBox="0 0 24 24" dangerouslySetInnerHTML={{ __html: IC[name] || '' }} />
)

// ---- built-in mock (ported verbatim from the prototype's LENSES) -----------
// Used as the fallback whenever the briefing endpoint 404s / errors, and as the
// per-lens meta (name / icon / color) that the API responses are merged onto.
const LENSES = [
  { id: 'exec', name: 'Executive', icon: 'gauge', color: '#4E9AC4', elab: 'Attributed revenue · 30 days', val: '$128,400', delta: '+12%', up: true,
    hist: [71, 74, 78, 80, 86, 84, 90, 96, 101, 110, 118, 122, 128], fc: [128, 131, 135, 138, 142], unit: 'k',
    forecast: 'On pace for <b>$142k</b> by month-end — <b>+11%</b> — if current channel mix holds.',
    narr: "Growth is real but <b>lopsided</b>: Google Shopping and Shopify repeat orders carry it, while Meta prospecting (ROAS 2.6×) quietly drags the blend down.",
    sugs: [{ t: 'Shift 20% of Meta prospecting budget into Google Shopping (your 4.1× line).', tech: 'Reallocation', imp: '+~$6k/mo', conf: 'high' },
           { t: 'Add a post-purchase upsell flow — your repeat-order rate is your strongest signal.', tech: 'Peak-end rule', imp: '+AOV', conf: 'medium' }],
    sec: { type: 'donut', title: 'Where revenue comes from', rows: [['Google Ads', 42, '#E24A3B'], ['Meta Ads', 31, '#4E9AC4'], ['Organic', 18, '#4FAE5A'], ['Email', 9, '#8E7CC3']] } },
  { id: 'acq', name: 'Acquisition', icon: 'target', color: '#E24A3B', elab: 'Blended ROAS · 30 days', val: '3.4×', delta: '-0.2×', up: false,
    hist: [3.9, 3.8, 3.7, 3.7, 3.6, 3.5, 3.5, 3.4, 3.4, 3.4], fc: [3.4, 3.3, 3.2, 3.1, 3.1], unit: 'x',
    forecast: 'Trending to <b>3.1×</b> in two weeks if Meta prospecting keeps scaling at current efficiency.',
    narr: "You're <b>buying more volume at worse efficiency</b>. Google search stays strong (4.1×); the slide is entirely Meta prospecting and one broad-match Google campaign.",
    sugs: [{ t: 'Pause the two Meta prospecting ad sets below 2.0× — no qualified pipeline attributed to them.', tech: 'Loss aversion', imp: 'save $8.4k', conf: 'medium' },
           { t: 'Move budget to retargeting + Shopping where intent is highest.', tech: 'Intent tiering', imp: '+ROAS', conf: 'high' }],
    sec: { type: 'bars', title: 'ROAS by channel', unit: 'x', rows: [['Google Search', 4.1, '#E24A3B'], ['Email', 3.8, '#8E7CC3'], ['Shopping', 3.6, '#4FAE5A'], ['Meta Ads', 2.6, '#4E9AC4'], ['TikTok', 1.9, '#C64B8B']] } },
  { id: 'conv', name: 'Conversion', icon: 'filter', color: '#8E7CC3', elab: 'Site conversion rate', val: '2.1%', delta: '+0.1%', up: true,
    hist: [1.7, 1.8, 1.8, 1.9, 1.9, 2.0, 2.0, 2.0, 2.1, 2.1], fc: [2.1, 2.2, 2.3, 2.4, 2.5], unit: '%',
    forecast: 'Fixing the checkout leak could lift you to <b>2.5%</b> — roughly <b>+$18k</b>/mo at current traffic.',
    narr: "High-intent traffic arrives, then <b>stalls at add-to-cart → order</b> (a 59% drop). The demo/checkout page has four competing CTAs and no proof near the button.",
    sugs: [{ t: 'Test one primary CTA on mobile checkout with a client result placed before the button.', tech: 'Choice overload', imp: '+form completion', conf: 'medium' },
           { t: 'Replace jargon microcopy with plain, scannable steps.', tech: 'Processing fluency', imp: '+CVR', conf: 'medium' }],
    sec: { type: 'funnel', title: 'Conversion funnel', rows: [['Impressions', 210000], ['Clicks', 8400], ['Sessions', 6100], ['Add to cart', 1240], ['Orders', 512]] } },
  { id: 'seo', name: 'SEO', icon: 'globe', color: '#EAB43C', elab: 'Organic clicks · 30 days', val: '8,420', delta: '+35%', up: true,
    hist: [5.1, 5.4, 5.8, 6.0, 6.4, 6.8, 7.1, 7.6, 8.0, 8.4], fc: [8.4, 8.9, 9.3, 9.6, 9.9], unit: 'k',
    forecast: 'Organic keeps climbing (<b>~9.9k</b> next month) — but pipeline stays flat unless you capture the intent.',
    narr: "SEO is winning <b>awareness</b> traffic, not <b>decision</b> traffic. 37 pages rank with high impressions but low CTR, and your commercial pages barely surface.",
    sugs: [{ t: 'Build commercial pages around problems/outcomes and internally link from your top informational winners.', tech: 'Search intent + internal linking', imp: '+qualified organic', conf: 'high' },
           { t: 'Rewrite titles/meta on the 37 high-impression, low-CTR pages.', tech: 'CTR optimization', imp: '+clicks', conf: 'high' }],
    sec: { type: 'bars', title: 'High-impression, low-CTR pages', unit: '%', rows: [['/guides/roas', 2.1, '#EAB43C'], ['/what-is-ltv', 1.8, '#EAB43C'], ['/blog/funnels', 1.5, '#EAB43C'], ['/glossary/cac', 1.2, '#EAB43C']] } },
  { id: 'commerce', name: 'Commerce', icon: 'bag', color: '#4FAE5A', elab: 'Orders · 30 days', val: '512', delta: '+15%', up: true,
    hist: [380, 395, 410, 430, 445, 458, 470, 486, 500, 512], fc: [512, 528, 545, 560, 575], unit: '',
    forecast: 'On pace for <b>~575 orders</b> next month; AOV holding at $251.',
    narr: "Volume is healthy and <b>concentrated</b>: three SKUs drive 61% of revenue. Cart abandonment is your biggest untapped lever.",
    sugs: [{ t: 'Trigger a 1-hour cart-recovery message via WhatsApp on your top-3 SKUs.', tech: 'Timely nudge', imp: '+recovered carts', conf: 'medium' },
           { t: 'Bundle the two SKUs most often bought together.', tech: 'Anchoring', imp: '+AOV', conf: 'medium' }],
    sec: { type: 'bars', title: 'Top products by revenue', unit: '$k', rows: [['Performance Polo', 23.4, '#4FAE5A'], ['Tour Shorts', 18.7, '#4FAE5A'], ['Quarter Zip', 14.1, '#4FAE5A'], ['Cap', 6.2, '#4FAE5A']] } },
  { id: 'retention', name: 'Retention', icon: 'repeat', color: '#C64B8B', elab: 'Repeat purchase rate', val: '28%', delta: '+3pt', up: true,
    hist: [22, 23, 23, 24, 25, 25, 26, 27, 27, 28], fc: [28, 29, 30, 31, 32], unit: '%',
    forecast: 'Trending to <b>32%</b> if the post-purchase experience improves — repeat buyers are 3× more profitable.',
    narr: "First-order experience is strong; the <b>second-order nudge is missing</b>. Most churn happens in the 30–60 day gap with no touch.",
    sugs: [{ t: 'End onboarding on a high note, then a value check-in at day 21.', tech: 'Peak-end + fresh-start', imp: '+repeat rate', conf: 'medium' },
           { t: 'Segment one-time buyers of your hero SKU for a tailored winback.', tech: 'Segmentation', imp: '+LTV', conf: 'medium' }],
    sec: { type: 'bars', title: 'Orders by customer cohort', unit: '', rows: [['1st order', 512, '#C64B8B'], ['2nd order', 143, '#C64B8B'], ['3rd+', 88, '#C64B8B']] } },
  { id: 'forecast', name: 'Forecast', icon: 'crystal', color: '#5B6BE8', elab: 'Projected revenue · next 30 days', val: '$142k', delta: '+11%', up: true,
    hist: [96, 101, 110, 118, 122, 128], fc: [128, 133, 137, 140, 142, 146], unit: 'k',
    forecast: 'Base case <b>$142k</b>. Fix the checkout leak and it\'s <b>$160k</b>; let Meta keep dragging and it\'s <b>$134k</b>.',
    narr: "Three levers move the number: <b>the funnel leak</b> (biggest upside), <b>Meta efficiency</b> (biggest risk), and <b>repeat rate</b> (slow compounder).",
    sugs: [{ t: 'Prioritize the checkout experiment — it has the highest modeled upside (+$18k).', tech: 'Highest-EV first', imp: '+$18k', conf: 'medium' },
           { t: 'Set a guardrail: auto-flag any channel that falls below 2.0× ROAS.', tech: 'Risk control', imp: 'protect margin', conf: 'high' }],
    sec: { type: 'scenario', title: 'Scenarios', rows: [['Fix funnel leak', '#4FAE5A', '$160k', '+25%'], ['Base case', '#5B6BE8', '$142k', '+11%'], ['Meta keeps dragging', '#E24A3B', '$134k', '+5%']] } },
]
const lensById = (id) => LENSES.find((x) => x.id === id) || LENSES[0]

// ---- ported CSS (verbatim from the prototype, scoped under .ib-*) -----------
const CSS = `
.ib-root{
  --ib-bg:#FBFBF7; --ib-panel:#FFFFFF; --ib-panel2:#F6F6F0; --ib-line:#ECEBE3; --ib-ink:#2E3A45; --ib-mut:#7C8794; --ib-dim:#AEB6BD;
  --ib-accent:#4E9AC4; --ib-up:#4FAE5A; --ib-down:#E24A3B;
  --ib-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.07);
  --ib-shadow-lg:0 10px 24px rgba(46,58,69,.10),0 34px 66px rgba(46,58,69,.15);
  font-family:Nunito,system-ui,sans-serif;color:var(--ib-ink);-webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .ib-root{
  --ib-bg:#0E1116; --ib-panel:#171B22; --ib-panel2:#12161C; --ib-line:#262C36; --ib-ink:#E7ECF1; --ib-mut:#95A0AD; --ib-dim:#5C6672;
  --ib-accent:#5FB0DB; --ib-up:#5CC47C; --ib-down:#F0575C;
  --ib-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --ib-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 34px 70px rgba(0,0,0,.6);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .ib-root{
  --ib-bg:#0E1116; --ib-panel:#171B22; --ib-panel2:#12161C; --ib-line:#262C36; --ib-ink:#E7ECF1; --ib-mut:#95A0AD; --ib-dim:#5C6672;
  --ib-accent:#5FB0DB; --ib-up:#5CC47C; --ib-down:#F0575C;
  --ib-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --ib-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 34px 70px rgba(0,0,0,.6);
}}
.ib-root *{box-sizing:border-box}
.ib-root svg{display:block}
.ib-root h1{font-size:24px;font-weight:800;margin:0;letter-spacing:-.02em;}
.ib-root .ib-sub{margin:4px 0 0;color:var(--ib-mut);font-size:14px;font-weight:500;}
.ib-ribbon{display:inline-flex;align-items:center;gap:7px;margin:12px 0 0;padding:6px 12px;border-radius:20px;
  background:rgba(234,180,60,.14);border:1px solid rgba(234,180,60,.35);color:#B4841B;font:700 11.5px Nunito;}
[data-theme="dark"] .ib-ribbon{color:#EAB43C;}
.ib-ribbon .ib-dot{width:7px;height:7px;border-radius:50%;background:#EAB43C;flex:none;}

/* lens rail */
.ib-lenses{display:flex;gap:8px;margin:18px 0 4px;overflow-x:auto;padding-bottom:6px;}
.ib-lens{flex:none;display:flex;align-items:center;gap:8px;background:var(--ib-panel);border:1px solid var(--ib-line);border-radius:14px;
  padding:9px 14px;font:700 13px Nunito;color:var(--ib-mut);cursor:pointer;transition:.16s;box-shadow:var(--ib-shadow);}
.ib-lens svg{width:16px;height:16px;stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
.ib-lens:hover{color:var(--ib-ink);}
.ib-lens.active{color:#fff;border-color:transparent;}

/* briefing */
.ib-brief{margin-top:14px;display:grid;grid-template-columns:1.65fr 1fr;gap:16px;align-items:start;}
@media(max-width:800px){.ib-brief{grid-template-columns:1fr}}
.ib-card{background:var(--ib-panel);border:1px solid var(--ib-line);border-radius:20px;box-shadow:var(--ib-shadow);padding:20px 22px;}
.ib-hero .ib-htop{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;}
.ib-hero .ib-elab{font-size:12.5px;font-weight:700;color:var(--ib-mut);text-transform:uppercase;letter-spacing:.06em;margin:0;}
.ib-hero .ib-eval{font-size:38px;font-weight:800;letter-spacing:-.03em;margin:4px 0 0;font-variant-numeric:tabular-nums;}
.ib-delta{font-size:13px;font-weight:800;padding:5px 11px;border-radius:20px;display:inline-flex;gap:5px;align-items:center;}
.ib-delta.up{background:rgba(79,174,90,.16);color:var(--ib-up);}
.ib-delta.down{background:rgba(226,74,59,.16);color:var(--ib-down);}
.ib-fnote{margin:14px 0 0;font-size:13.5px;font-weight:700;color:var(--ib-ink);background:var(--ib-panel2);border-radius:12px;padding:11px 14px;display:flex;gap:9px;align-items:center;}
.ib-fnote .ib-fi{width:26px;height:26px;flex:none;border-radius:8px;background:var(--ib-accent);display:flex;align-items:center;justify-content:center;}
.ib-fnote .ib-fi svg{width:15px;height:15px;stroke:#fff;fill:none;stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round;}
.ib-fnote b{color:var(--ib-accent);}

/* intelligence panel */
.ib-intel h3{margin:0 0 4px;font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:var(--ib-mut);display:flex;align-items:center;gap:7px;}
.ib-intel h3 svg{width:15px;height:15px;stroke:var(--ib-accent);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}
.ib-narr{font-size:14px;font-weight:600;line-height:1.6;color:var(--ib-ink);margin:0 0 4px;}
.ib-narr b{color:var(--ib-accent);}
.ib-sugs{margin-top:14px;display:flex;flex-direction:column;gap:10px;}
.ib-sug{border:1px solid var(--ib-line);border-radius:14px;padding:12px 13px;background:var(--ib-panel2);cursor:pointer;transition:.16s;}
.ib-sug:hover{border-color:var(--ib-accent);transform:translateY(-1px);}
.ib-sug .ib-st{font-size:13.5px;font-weight:700;line-height:1.4;margin:0;}
.ib-sug .ib-sb{display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-top:8px;}
.ib-tech{font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;padding:3px 8px;border-radius:7px;background:var(--ib-accent);color:#fff;}
.ib-imp{font-size:11.5px;font-weight:700;color:var(--ib-up);}
.ib-conf{font-size:11px;font-weight:700;color:var(--ib-mut);margin-left:auto;}

/* secondary chart */
.ib-sec{margin-top:16px;}
.ib-sec .ib-shd{display:flex;justify-content:space-between;align-items:baseline;margin:0 0 4px;}
.ib-sec .ib-shd h4{margin:0;font-size:15px;font-weight:800;}
.ib-sec .ib-shd span{font-size:12px;font-weight:600;color:var(--ib-dim);}
.ib-bars{margin-top:12px;}
.ib-bar-row{display:flex;align-items:center;gap:11px;margin:9px 0;cursor:pointer;}
.ib-bar-row .ib-bl{width:110px;font-size:12.5px;font-weight:700;color:var(--ib-mut);flex:none;}
.ib-bar-track{flex:1;height:26px;background:var(--ib-panel2);border-radius:8px;overflow:hidden;}
.ib-bar-fill{height:100%;border-radius:8px;transform-origin:left;animation:ib-grow .7s cubic-bezier(.3,1,.4,1) both;transition:filter .15s;}
.ib-bar-row:hover .ib-bar-fill{filter:brightness(1.08);}
@keyframes ib-grow{from{transform:scaleX(0)}to{transform:scaleX(1)}}
.ib-bar-row .ib-bv{width:64px;text-align:right;font-size:13px;font-weight:800;font-variant-numeric:tabular-nums;flex:none;}
.ib-funnel .ib-fst{cursor:pointer;margin:8px 0;}
.ib-funnel .ib-ft{display:flex;justify-content:space-between;font-size:12.5px;font-weight:700;margin-bottom:4px;}
.ib-funnel .ib-ft span:first-child{color:var(--ib-mut);}
.ib-ftrack{height:28px;background:var(--ib-panel2);border-radius:8px;overflow:hidden;}
.ib-ffill{height:100%;border-radius:8px;animation:ib-grow .7s cubic-bezier(.3,1,.4,1) both;}
.ib-funnel .ib-drop{font-size:11px;font-weight:700;color:var(--ib-dim);margin-top:2px;}

/* drill drawer */
.ib-drawer{position:fixed;top:0;right:0;height:100%;width:340px;max-width:88%;background:var(--ib-panel);border-left:1px solid var(--ib-line);
  box-shadow:-20px 0 50px rgba(20,26,32,.14);transform:translateX(103%);transition:transform .32s cubic-bezier(.4,0,.15,1);z-index:90;display:flex;flex-direction:column;}
.ib-drawer.open{transform:none;}
.ib-drawer .ib-dh{padding:18px 20px 14px;border-bottom:1px solid var(--ib-line);display:flex;justify-content:space-between;align-items:flex-start;gap:12px;}
.ib-drawer .ib-dh .ib-k{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--ib-mut);margin:0 0 5px;}
.ib-drawer .ib-dh h3{margin:0;font-size:18px;font-weight:800;}
.ib-drawer .ib-db{padding:18px 20px;overflow:auto;}
.ib-xb{background:none;border:none;color:var(--ib-mut);font-size:22px;cursor:pointer;line-height:1;}
.ib-kv .ib-r{display:flex;justify-content:space-between;padding:9px 0;border-bottom:1px solid var(--ib-line);font-size:13px;}
.ib-kv .ib-r span:first-child{color:var(--ib-mut);font-weight:600;}.ib-kv .ib-r span:last-child{font-weight:800;font-variant-numeric:tabular-nums;}
.ib-evi{font-size:11.5px;color:var(--ib-dim);margin-top:14px;padding-top:12px;border-top:1px dashed var(--ib-line);line-height:1.55;font-weight:600;}
.ib-draft{border:none;border-radius:20px;margin-top:16px;width:100%;padding:12px;font:800 13px Nunito;color:#fff;cursor:pointer;}
.ib-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)}
.ib-backdrop{position:fixed;inset:0;background:rgba(20,26,32,.28);opacity:0;pointer-events:none;transition:.25s;z-index:89;}
.ib-backdrop.open{opacity:1;pointer-events:auto;}
`

// bar value formatting (matches the prototype's secSVG bars)
function fmtBar(v, u) {
  // Guard against a row that carries neither pre-formatted text nor a numeric
  // value — otherwise `${pre}${v}${suf}` would render the literal "undefined".
  if (v == null || (typeof v === 'number' && Number.isNaN(v)) || v === '') return '—'
  const pre = (u === '$k' || u === '$') ? '$' : ''
  const suf = u === 'x' ? '×' : u === '%' ? '%' : u === '$k' ? 'k' : ''
  return `${pre}${v}${suf}`
}

// Normalize a secondary-chart row to one shape. The built-in mock ships tuples
// ([label, value, color] for donut/bars, [label, value] for funnel); the REAL
// backend ships objects — bars/donut {l,v,t,c}, funnel {l,v,t,cr} — so without
// this every live chart rendered empty labels and "undefined%". Accept both.
function normRow(r) {
  if (Array.isArray(r)) return { label: r[0], value: r[1], color: r[2], text: undefined, delta: r[3] }
  if (r && typeof r === 'object') return {
    label: r.l ?? r.label ?? r.name,
    value: r.v ?? r.value ?? r.pct,
    color: r.c ?? r.color,
    text: r.t ?? r.text,
    delta: r.delta,
    cr: r.cr,
  }
  return { label: r }
}
// Scenario rows carry a DIFFERENT tuple order: [label, color, value, delta].
// The backend object is {l,v,t} — prefer the formatted text for display.
function normScenarioRow(r) {
  if (Array.isArray(r)) return { label: r[0], color: r[1], value: r[2], delta: r[3] }
  if (r && typeof r === 'object') return {
    label: r.l ?? r.label ?? r.name,
    color: r.c ?? r.color,
    value: r.t ?? r.text ?? r.v ?? r.value,
    delta: r.delta ?? r.cr,
  }
  return { label: r }
}

// ---- secondary chart (ported from secSVG) ----------------------------------
function SecChart({ l, onDrill }) {
  const s = l.sec || {}
  const rawRows = Array.isArray(s.rows) ? s.rows : []

  // Tidy empty state — replaces the ugly empty chart when a lens has no breakdown
  // yet (sample / unconnected sources).
  if (!rawRows.length) {
    return (
      <div style={{ padding: '20px 4px', color: 'var(--ib-dim)', fontSize: 13, fontWeight: 700 }}>
        No breakdown yet — connect a source
      </div>
    )
  }

  if (s.type === 'donut') {
    const rows = rawRows.map(normRow)
    // Backend rows may lack a pct — derive each slice from value / sum(values).
    // For the mock (values already sum to 100) this is a no-op.
    const sum = rows.reduce((a, r) => a + (Number(r.value) || 0), 0) || 1
    const R = 54, C = 2 * Math.PI * R
    let acc = 0
    const segs = rows.map((r, i) => {
      const f = (Number(r.value) || 0) / sum
      const el = (
        <circle key={i} r={R} cx="80" cy="80" fill="none" stroke={r.color || l.color} strokeWidth="24"
          strokeDasharray={`${(f * C).toFixed(1)} ${C}`} strokeDashoffset={(-acc * C).toFixed(1)}
          transform="rotate(-90 80 80)" style={{ cursor: 'pointer' }} onClick={() => onDrill(r.label)} />
      )
      acc += f
      return el
    })
    return (
      <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <svg viewBox="0 0 160 160" width="150" height="150">{segs}</svg>
        <div style={{ flex: 1, minWidth: 120 }}>
          {rows.map((r, i) => {
            const pct = Math.round(((Number(r.value) || 0) / sum) * 100)
            return (
              <div key={i} className="ib-bar-row" style={{ margin: '6px 0' }} onClick={() => onDrill(r.label)}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: r.color || l.color, flex: 'none' }} />
                <div className="ib-bl" style={{ width: 'auto', flex: 1, color: 'var(--ib-ink)' }}>{r.label}</div>
                <div className="ib-bv" style={{ width: 'auto' }}>{r.text ?? `${pct}%`}</div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (s.type === 'funnel') {
    const rows = rawRows.map(normRow)
    const mx = Number(rows[0]?.value) || 1
    return (
      <div className="ib-funnel">
        {rows.map((f, i) => {
          const val = Number(f.value) || 0
          const w = 100 * val / mx
          const prev = i ? (Number(rows[i - 1].value) || 0) : val
          const drop = i ? Math.round((1 - val / (prev || 1)) * 100) : 0
          return (
            <div key={i} className="ib-fst" onClick={() => onDrill(f.label)}>
              <div className="ib-ft"><span>{f.label}</span><span>{f.text ?? val.toLocaleString()}</span></div>
              <div className="ib-ftrack">
                <div className="ib-ffill" style={{ width: `${Math.max(6, w)}%`, background: l.color, animationDelay: `${i * 0.08}s` }} />
              </div>
              {i ? <div className="ib-drop">▼ {drop}% drop from {rows[i - 1].label}</div> : null}
            </div>
          )
        })}
      </div>
    )
  }

  if (s.type === 'scenario') {
    const rows = rawRows.map(normScenarioRow)
    return (
      <>
        {rows.map((r, i) => (
          <div key={i} className="ib-bar-row" style={{ cursor: 'pointer', background: 'var(--ib-panel2)', border: '1px solid var(--ib-line)', borderRadius: 12, padding: '11px 13px', margin: '8px 0' }} onClick={() => onDrill(r.label)}>
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: r.color || l.color, flex: 'none' }} />
            <div className="ib-bl" style={{ width: 'auto', flex: 1, color: 'var(--ib-ink)', fontSize: 13.5 }}>{r.label}</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{r.value}</div>
            {r.delta != null && <div className="ib-conf" style={{ marginLeft: 10, color: r.color || l.color }}>{r.delta}</div>}
          </div>
        ))}
      </>
    )
  }

  // bars
  const rows = rawRows.map(normRow)
  const mx = Math.max(...rows.map((r) => Number(r.value) || 0), 0.0001)
  const u = s.unit || ''
  return (
    <div className="ib-bars">
      {rows.map((r, i) => (
        <div key={i} className="ib-bar-row" onClick={() => onDrill(r.label)}>
          <div className="ib-bl">{r.label}</div>
          <div className="ib-bar-track">
            <div className="ib-bar-fill" style={{ width: `${(Number(r.value) || 0) / mx * 100}%`, background: r.color || l.color }} />
          </div>
          {/* prefer the backend's pre-formatted text ('$23.4k' / '4.1x') when present */}
          <div className="ib-bv">{r.text ?? fmtBar(r.value, u)}</div>
        </div>
      ))}
    </div>
  )
}

// ---- drawer content builders (ported from drill / suggest) -----------------
function drillContent(l, k) {
  const s = l.sec || {}
  const rawRows = Array.isArray(s.rows) ? s.rows : []
  if (s.type === 'funnel') {
    const rows = rawRows.map(normRow)
    const i = rows.findIndex((r) => r.label === k)
    const f = rows[i] || { value: 0 }
    const prev = i > 0 ? rows[i - 1] : f
    const fv = Number(f.value) || 0
    const pv = Number(prev.value) || 0
    const drop = i > 0 ? Math.round((1 - fv / (pv || 1)) * 100) : 0
    return {
      kicker: 'Funnel stage', title: k,
      body: (
        <>
          <div style={{ fontSize: 30, fontWeight: 800 }}>{f.text ?? fv.toLocaleString()}</div>
          <div className="ib-kv">
            <div className="ib-r"><span>Conversion from {prev.label}</span><span>{i > 0 ? (100 - drop) + '%' : '—'}</span></div>
            <div className="ib-r"><span>Dropped here</span><span>{i > 0 ? (pv - fv).toLocaleString() + ' (' + drop + '%)' : '—'}</span></div>
          </div>
          <p className="ib-evi">Evidence: session + order data, last 30 days from GA4 + Shopify. {i > 0 && drop > 50 ? 'This is the leak the checkout experiment targets.' : ''}</p>
        </>
      ),
    }
  }
  if (s.type === 'scenario') {
    const rows = rawRows.map(normScenarioRow)
    const r = rows.find((x) => x.label === k) || { label: k }
    return {
      kicker: 'Scenario', title: k,
      body: (
        <>
          <div style={{ fontSize: 26, fontWeight: 800 }}>{r.value ?? ''}</div>
          <p className="ib-evi">Evidence: last 30 days vs prior 30, drawn from your connected sources. Drilling further would list the campaigns / pages / SKUs behind this number.</p>
        </>
      ),
    }
  }
  const rows = rawRows.map(normRow)
  const r = rows.find((x) => x.label === k) || { label: k }
  let value
  if (s.type === 'donut') {
    const sum = rows.reduce((a, x) => a + (Number(x.value) || 0), 0) || 1
    value = r.text ?? (r.value != null ? Math.round((Number(r.value) / sum) * 100) + '%' : '')
  } else {
    value = r.text ?? (r.value != null ? fmtBar(r.value, s.unit) : '') // bars
  }
  const kicker = s.type === 'donut' ? 'Channel' : 'Detail'
  return {
    kicker, title: k,
    body: (
      <>
        <div style={{ fontSize: 26, fontWeight: 800 }}>{value}</div>
        <p className="ib-evi">Evidence: last 30 days vs prior 30, drawn from your connected sources. Drilling further would list the campaigns / pages / SKUs behind this number.</p>
      </>
    ),
  }
}

function suggestContent(l, s, onDraft) {
  return {
    kicker: 'Recommended action · ' + s.tech, title: 'Turn this into a test',
    body: (
      <>
        <p className="ib-narr" style={{ fontSize: 14 }}>{s.t}</p>
        <div className="ib-kv">
          <div className="ib-r"><span>Technique</span><span>{s.tech}</span></div>
          <div className="ib-r"><span>Expected impact</span><span style={{ color: 'var(--ib-up)' }}>{s.imp}</span></div>
          <div className="ib-r"><span>Confidence</span><span>{s.conf}</span></div>
        </div>
        <p className="ib-evi">The Brain proposes this as a <b>hypothesis</b>, grounded in the evidence above — not a certainty. Approve it and Growth Lab turns it into an experiment card with a metric, guardrail, and control/variant.</p>
        <button className="ib-draft" style={{ background: l.color }} onClick={onDraft}>Draft this experiment in Growth Lab</button>
      </>
    ),
  }
}

// ---- main component --------------------------------------------------------
export default function MarketingDashboards({ from: fromProp, to: toProp } = {}) {
  const to = toProp || fmtDate(new Date())
  const from = fromProp || fmtDate(daysAgo(30))

  const [cur, setCur] = useState('exec')
  const [briefing, setBriefing] = useState(lensById('exec')) // merged lens meta + briefing data
  const [sample, setSample] = useState(false)                // show ribbon (sample flag or fallback)
  const [drawer, setDrawer] = useState(null)                 // { kicker, title, body } | null

  useEffect(() => { document.head.querySelector('link[data-ib-nunito]') || mountNunito() }, [])

  useEffect(() => {
    let alive = true
    const meta = lensById(cur)
    axios.get(`${API}/briefing/${cur}`, { params: { from, to } })
      .then((r) => {
        if (!alive) return
        const d = r.data || {}
        // merge briefing fields over the client-side lens meta so missing fields fall back
        setBriefing({ ...meta, ...d, id: meta.id, name: meta.name, icon: meta.icon, color: d.color || meta.color })
        setSample(!!d.sample)
      })
      .catch(() => {
        if (!alive) return
        // endpoint 404'd or errored → fall back to the prototype's built-in mock, clearly labelled
        setBriefing(meta)
        setSample(true)
      })
    return () => { alive = false }
  }, [cur, from, to])

  const l = briefing
  const rootStyle = useMemo(() => ({ '--ib-accent': l.color }), [l.color])

  const openDrill = (k) => setDrawer(drillContent(l, k))
  const openSuggest = (s) => setDrawer(suggestContent(l, s, () => {
    // 'Draft this experiment in Growth Lab' → navigate to the Growth Lab surface
    window.location.assign('/growth-lab')
  }))
  const closeDraw = () => setDrawer(null)

  const secLabel = l.sec?.type === 'scenario' ? 'click a scenario' : 'click to drill in'

  return (
    <div className="ib-root" style={rootStyle}>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <h2 className="ib-sr">Interactive marketing intelligence dashboard with dimension lenses, forecasts, and recommendations.</h2>

      <h1>Intelligence briefings</h1>
      <p className="ib-sub">Pick a lens. Each one forecasts where you're heading, explains why, and hands you the next move.</p>
      {sample && (
        <div className="ib-ribbon"><span className="ib-dot" /> sample — connect sources for live data</div>
      )}

      {/* lens rail */}
      <div className="ib-lenses">
        {LENSES.map((ln) => (
          <button key={ln.id} className={`ib-lens ${ln.id === cur ? 'active' : ''}`}
            style={ln.id === cur ? { background: ln.color } : undefined}
            onClick={() => setCur(ln.id)}>
            <Svg name={ln.icon} />{ln.name}
          </button>
        ))}
      </div>

      {/* briefing */}
      <div className="ib-brief">
        <div className="ib-card ib-hero">
          <div className="ib-htop">
            <div>
              <p className="ib-elab">{l.elab}</p>
              <div className="ib-eval">{l.val}</div>
            </div>
            <span className={`ib-delta ${l.up ? 'up' : 'down'}`}>{l.up ? '▲' : '▼'} {l.delta}</span>
          </div>
          {/* Financial-style time-series explorer — replaces the old thin forecast line.
              Candles / Area / Bars with interval, date-range nav and dimension tabs. */}
          <TimeSeriesExplorer
            metric={LENS_METRIC[l.id] || 'revenue'}
            title={`${l.name} over time`}
            color={l.color}
          />
          <div className="ib-fnote">
            <span className="ib-fi"><Svg name="crystal" /></span>
            <div dangerouslySetInnerHTML={{ __html: l.forecast || '' }} />
          </div>
          <div className="ib-sec">
            <div className="ib-shd"><h4>{l.sec?.title}</h4><span>{secLabel}</span></div>
            <SecChart l={l} onDrill={openDrill} />
          </div>
        </div>

        <div className="ib-card ib-intel">
          <h3><Svg name="spark" /> What's happening</h3>
          <p className="ib-narr" dangerouslySetInnerHTML={{ __html: l.narr || '' }} />
          <h3 style={{ marginTop: 18 }}><Svg name="bulb" /> The Brain suggests</h3>
          <div className="ib-sugs">
            {(Array.isArray(l.sugs) ? l.sugs : []).map((s, i) => (
              <div key={i} className="ib-sug" onClick={() => openSuggest(s)}>
                <p className="ib-st">{s.t}</p>
                <div className="ib-sb">
                  <span className="ib-tech">{s.tech}</span>
                  <span className="ib-imp">{s.imp}</span>
                  <span className="ib-conf">confidence: {s.conf}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* drill drawer */}
      <div className={`ib-backdrop ${drawer ? 'open' : ''}`} onClick={closeDraw} />
      <div className={`ib-drawer ${drawer ? 'open' : ''}`}>
        <div className="ib-dh">
          <div>
            <p className="ib-k">{drawer?.kicker || 'Detail'}</p>
            <h3>{drawer?.title || '—'}</h3>
          </div>
          <button className="ib-xb" onClick={closeDraw} aria-label="Close">×</button>
        </div>
        <div className="ib-db">{drawer?.body}</div>
      </div>
    </div>
  )
}

// Load the Nunito webfont once (the prototype's typographic identity).
function mountNunito() {
  if (typeof document === 'undefined') return
  const add = (href, extra) => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'; link.href = href; link.setAttribute('data-ib-nunito', '1')
    if (extra) Object.assign(link, extra)
    document.head.appendChild(link)
  }
  add('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap')
}
